/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/indent */
import type { ComponentProps } from "react";
import React, { useCallback, useState } from "react";
import {
	Box,
	ButtonLayout,
	Callout,
	Grid,
	GridItem,
	IconInformationUserLight,
	IconLockClosedLight,
	skinVars,
	useScreenSize,
} from "@telefonica/mistica";
import { strings } from "../../../localization";
import type { CheckBoxTermsContractProps } from "../../atoms";
import { Button, CheckBoxTermsContract } from "../../atoms";
import type { HeroDataInfoProps, ValidatedProps } from "../../organisms";
import { HeroDataInfo, InputsNewPassword, ScreenResponsive } from "../../organisms";
import { observer } from "mobx-react-lite";
import { useNavigateCustom } from "../../../hooks";

interface InputProps {
	label: string;
	name: string;
}

interface NewTransactionalPasswordProps {
	heroDataInfo?: HeroDataInfoProps;
	firstInput?: InputProps;
	secondInput?: InputProps;
	callout?: ComponentProps<typeof Callout>;
	terms?: CheckBoxTermsContractProps;
	isCalloutVisible?: boolean;
	isCheckBoxTermsContractVisible?: boolean;
	pathLinkTerms?: string;
	linkExternal?: string;
	newTab?: boolean;
	textButton?: string;
	isDisabledButton?: boolean;
	isLoadingButton?: boolean;
	onValueInput(value: string): void;
	onActionButton(): Promise<void> | void;
  handleChckboxValueAnalyticEvent?(description?: string): void;
  handleCheckboxLinkAnalyticEvent?(description: string): void;
  handleErrorFormEvent?(errorText: string): void;
}

const { anticipateFGTS, buttonPrimary } = strings.components.buttons;
const { calloutDescription, title, termsText, description } = strings.templates.NewTransactionalPassword;

export const NewTransactionalPassword: React.FC<NewTransactionalPasswordProps> = observer(
	({
		heroDataInfo,
		firstInput,
		secondInput,
		callout,
		terms,
		isCalloutVisible = true,
		isCheckBoxTermsContractVisible = true,
		pathLinkTerms,
		linkExternal,
		newTab = false,
		textButton,
		onValueInput,
		onActionButton,
		isDisabledButton = false,
		isLoadingButton = false,
    handleCheckboxLinkAnalyticEvent,
    handleChckboxValueAnalyticEvent,
    handleErrorFormEvent,
	}) => {
		const { isDesktopOrBigger } = useScreenSize();
		const navigation = useNavigateCustom();
		const [checked, setChecked] = useState(false);
		const [isValidCombineInputs, setIsValidCombineInputs] = useState(false);
		// Valida se os inputs de senha e confirmação de senha são válidos.
		// retorna o valor para onValueInput.
		const handleValueInput = useCallback((inputValues: ValidatedProps ): void => {
      setIsValidCombineInputs(inputValues.isValid);
			onValueInput(inputValues.value);
    }, []);

		// Navega para a página anterior.
		const onBack = (): void => {
			navigation(-1);
		};

		return (
			<ScreenResponsive navigationTitle={anticipateFGTS} onBack={onBack}>
				<Box paddingBottom={isDesktopOrBigger ? 24 : 0}>
					<Box paddingBottom={isDesktopOrBigger ? 80 : 32} paddingTop={isDesktopOrBigger ? 40 : 32}>
						<Grid columns={isDesktopOrBigger ? 4 : 1} justifyContent={"center"}>
							<GridItem columnStart={2} span={isDesktopOrBigger ? 2 : 1}>
								<Box width={isDesktopOrBigger ? 600 : "100%"}>
									<Box paddingBottom={isDesktopOrBigger ? 40 : 24}>
										<HeroDataInfo
											description={description}
											title={title}
											icon={<IconLockClosedLight size={64} color={skinVars.colors.brand} />}
											{...heroDataInfo}
										/>
									</Box>
									<Grid columns={{ maxSize: 392 }} justifyContent={"center"}>
										<Box paddingBottom={isDesktopOrBigger ? 16 : 16}>
											<InputsNewPassword
												isValidated={(inputValues) => handleValueInput(inputValues)}
												propsPasswordField={
													firstInput
														? {
																label: firstInput.label,
																name: firstInput.name,
														  }
														: undefined
												}
												propsRepeatPasswordField={
													secondInput
														? {
																label: secondInput.label,
																name: secondInput.name,
														  }
														: undefined
												}
                        handleErrorFormEvent={handleErrorFormEvent}
											/>
										</Box>

										{isCalloutVisible && (
											<Box paddingBottom={isDesktopOrBigger ? 40 : 32}>
												<Callout
													description={calloutDescription}
													icon={<IconInformationUserLight size={24} color={skinVars.colors.brand} />}
													{...callout}
												/>
											</Box>
										)}

										{isCheckBoxTermsContractVisible && (
											<CheckBoxTermsContract
												firstText={termsText[0]}
												linkText={termsText[1]}
												hrefPath={pathLinkTerms || ""}
												linkExternal={linkExternal}
												newTab={newTab}
												name="checkbox-create-password"
												onValue={(value) => {
                          setChecked(value);
                          if (handleChckboxValueAnalyticEvent) {
                            handleChckboxValueAnalyticEvent("confirmar-termos");
                          }
                        }}
                        onClickLink={() => {
                          if (handleCheckboxLinkAnalyticEvent) {
                            handleCheckboxLinkAnalyticEvent("termos-de-criacao-conta");
                            if (newTab) {
                              return window.open(`/fgts${pathLinkTerms}`, "_blank");
                            }
                            window.open(`/fgts${pathLinkTerms}`, "_self");
                          }
                          return undefined;
                        }}
												{...terms}
											/>
										)}

										<Box paddingTop={isDesktopOrBigger ? 32 : 16} paddingBottom={isDesktopOrBigger ? 16 : 48}>
											<ButtonLayout>
												<Button
													textButton={textButton || buttonPrimary}
													onAction={onActionButton}
													isDisabled={
														(isCheckBoxTermsContractVisible && !checked) || !isValidCombineInputs || isDisabledButton
													}
													isLoading={isLoadingButton}
													testID="button-primary-transactional-password"
												/>
											</ButtonLayout>
										</Box>
									</Grid>
								</Box>
							</GridItem>
						</Grid>
					</Box>
				</Box>
			</ScreenResponsive>
		);
	},
);
